import React from 'react';
import DatePicker from 'react-datepicker';
import { CalendarIcon } from '@heroicons/react/24/outline';
import "react-datepicker/dist/react-datepicker.css";

interface DateSelectionProps {
  checkIn: Date | null;
  checkOut: Date | null;
  onSelect: (dates: { checkIn: Date | null; checkOut: Date | null }) => void;
}

const DateSelection: React.FC<DateSelectionProps> = ({
  checkIn,
  checkOut,
  onSelect
}) => {
  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    onSelect({ checkIn: start, checkOut: end });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">
        When would you like to stay?
      </h2>
      
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <CalendarIcon className="h-8 w-8 text-airbnb-red" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Select your dates
            </h3>
            <p className="text-sm text-gray-500">
              Add your travel dates for exact pricing
            </p>
          </div>
        </div>

        <div className="mt-4">
          <DatePicker
            selected={checkIn}
            onChange={handleDateChange}
            startDate={checkIn}
            endDate={checkOut}
            selectsRange
            inline
            monthsShown={2}
            minDate={new Date()}
            calendarClassName="!border-0 !shadow-none"
            wrapperClassName="!block"
            dayClassName={date => 
              date.getTime() === checkIn?.getTime()
                ? "!bg-airbnb-red !text-white !rounded-l-full"
                : date.getTime() === checkOut?.getTime()
                ? "!bg-airbnb-red !text-white !rounded-r-full"
                : checkIn && checkOut && date > checkIn && date < checkOut
                ? "!bg-red-50"
                : ""
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Check-in
            </label>
            <DatePicker
              selected={checkIn}
              onChange={date => handleDateChange([date, checkOut])}
              dateFormat="MMM d, yyyy"
              placeholderText="Select check-in date"
              className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent transition-colors"
            />
          </div>
          
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Check-out
            </label>
            <DatePicker
              selected={checkOut}
              onChange={date => handleDateChange([checkIn, date])}
              dateFormat="MMM d, yyyy"
              placeholderText="Select check-out date"
              minDate={checkIn || new Date()}
              className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-airbnb-red focus:border-transparent transition-colors"
            />
          </div>
        </div>

        {checkIn && checkOut && (
          <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-100">
            <p className="text-sm text-green-800">
              <span className="font-medium">Perfect!</span> You've selected {checkIn.toLocaleDateString()} - {checkOut.toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateSelection;